import { endpoints } from "../../services/endpoints";
import { deleteJson, getJson, postJson, putJson, request } from "../../services/httpClient";

function extractData(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload?.data && typeof payload.data === "object") {
    return [payload.data];
  }
  return [];
}

function extractFirst(payload) {
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return payload.data;
  }
  const items = extractData(payload);
  if (items.length > 0) {
    return items[0];
  }
  if (payload && typeof payload === "object" && !Array.isArray(payload) && !("data" in payload)) {
    return payload;
  }
  return null;
}

const referenceListParams = {
  sortBy: "id",
  sortDir: "asc",
};

export async function fetchAllProducts(signal) {
  const payload = await getJson(endpoints.admin.products.all, {
    requiresAuth: true,
    signal,
  });
  return extractData(payload);
}

export async function fetchProductsPage(filters = {}, signal) {
  const payload = await getJson(endpoints.admin.products.list, {
    params: filters,
    requiresAuth: true,
    signal,
  });

  return extractFirst(payload) || {
    items: [],
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  };
}

export async function createProduct(payload) {
  const response = await postJson(endpoints.admin.products.create, payload, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function updateProduct(productId, payload) {
  const response = await putJson(endpoints.admin.products.update(productId), payload, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function uploadProductImage(file) {
  const formData = new FormData();
  formData.append("file", file);

  const payload = await request(endpoints.admin.products.uploadImage, {
    method: "POST",
    body: formData,
    requiresAuth: true,
  });

  if (typeof payload === "string") {
    return payload;
  }

  return payload?.data?.[0]?.imageUrl || payload?.data?.[0] || payload?.url || "";
}

export async function fetchAllCategories(signal) {
  const payload = await getJson(endpoints.admin.categories.all, {
    params: referenceListParams,
    requiresAuth: true,
    signal,
  });

  return extractData(payload);
}

export async function fetchCategoriesPage(filters = {}, signal) {
  const payload = await getJson(endpoints.admin.categories.all, {
    params: referenceListParams,
    requiresAuth: true,
    signal,
  });

  const items = extractData(payload);

  return {
    items,
    page: 0,
    size: items.length || Number(filters.size) || 10,
    totalElements: items.length,
    totalPages: items.length ? 1 : 0,
    hasNext: false,
    hasPrevious: false,
  };
}

export async function fetchCategoryById(categoryId, signal) {
  const payload = await getJson(endpoints.admin.categories.byId(categoryId), {
    requiresAuth: true,
    signal,
  });

  return extractFirst(payload);
}

export async function saveCategory(payload) {
  const response = await postJson(endpoints.admin.categories.save, payload, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function fetchAllBrands(signal) {
  const payload = await getJson(endpoints.admin.brands.all, {
    params: referenceListParams,
    requiresAuth: true,
    signal,
  });

  return extractData(payload);
}

export async function fetchBrandsPage(filters = {}, signal) {
  const payload = await getJson(endpoints.admin.brands.all, {
    params: referenceListParams,
    requiresAuth: true,
    signal,
  });

  const items = extractData(payload);

  return {
    items,
    page: 0,
    size: items.length || Number(filters.size) || 10,
    totalElements: items.length,
    totalPages: items.length ? 1 : 0,
    hasNext: false,
    hasPrevious: false,
  };
}

export async function fetchBrandById(brandId, signal) {
  const payload = await getJson(endpoints.admin.brands.byId(brandId), {
    requiresAuth: true,
    signal,
  });

  return extractFirst(payload);
}

export async function saveBrand(payload) {
  const response = await postJson(endpoints.admin.brands.save, payload, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function fetchOrdersPage(filters = {}, signal) {
  const payload = await postJson(
    endpoints.admin.orders.search,
    {
      searchText: String(filters.searchText || "").trim(),
      status: filters.status && filters.status !== "all" ? filters.status : "",
      page: Number(filters.page) || 0,
      size: Number(filters.size) || 10,
    },
    {
      requiresAuth: true,
      signal,
    }
  );

  const pageData = extractFirst(payload) || {};

  return {
    items: Array.isArray(pageData.orders) ? pageData.orders : [],
    page: Number(pageData.page) || 0,
    size: Number(pageData.size) || Number(filters.size) || 10,
    totalElements: Number(pageData.totalElements) || 0,
    totalPages: Number(pageData.totalPages) || 0,
    hasNext: Boolean(pageData.hasNext),
    hasPrevious: Boolean(pageData.hasPrevious),
  };
}

export async function fetchOrderDetails(orderId, signal) {
  const payload = await getJson(endpoints.admin.orders.details(orderId), {
    requiresAuth: true,
    signal,
  });

  return extractFirst(payload);
}

export async function updateOrderStatus(orderId, payload) {
  const response = await request(endpoints.admin.orders.updateStatus(orderId), {
    method: "PUT",
    body: payload,
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function cancelOrder(orderId, payload) {
  const response = await postJson(endpoints.admin.orders.cancel(orderId), payload || {}, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function requestOrderRefund(orderId, payload) {
  const response = await postJson(endpoints.admin.orders.refund(orderId), payload, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function fetchOrderItemSubstitutes(orderId, orderItemId, signal) {
  const payload = await getJson(endpoints.admin.orders.substitutes(orderId, orderItemId), {
    requiresAuth: true,
    signal,
  });

  return extractData(payload);
}

export async function substituteOrderItem(orderId, orderItemId, payload) {
  const response = await postJson(
    endpoints.admin.orders.substitute(orderId, orderItemId),
    payload,
    {
      requiresAuth: true,
    }
  );

  return extractFirst(response);
}

export async function markOrderItemUnavailable(orderId, orderItemId, payload) {
  const response = await postJson(
    endpoints.admin.orders.unavailable(orderId, orderItemId),
    payload || {},
    {
      requiresAuth: true,
    }
  );

  return extractFirst(response);
}

export async function fetchCheckoutSettings(signal) {
  const payload = await getJson(endpoints.admin.checkoutSettings.current, {
    requiresAuth: true,
    signal,
  });

  return extractFirst(payload);
}

export async function updateCheckoutSettings(payload) {
  const response = await putJson(endpoints.admin.checkoutSettings.current, payload, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function fetchServiceablePincodes(signal) {
  const payload = await getJson(endpoints.admin.checkoutSettings.serviceablePincodes, {
    requiresAuth: true,
    signal,
  });

  return extractData(payload);
}

export async function updateServiceablePincodes(payload) {
  const response = await putJson(endpoints.admin.checkoutSettings.serviceablePincodes, payload, {
    requiresAuth: true,
  });

  return extractData(response);
}

export async function toggleServiceablePincodeStatus(id, enabled) {
  const response = await putJson(
    endpoints.admin.checkoutSettings.serviceablePincodeStatus(id),
    { enabled },
    { requiresAuth: true }
  );

  return extractFirst(response);
}

export async function fetchUnserviceableDemands(signal) {
  const payload = await getJson(endpoints.admin.checkoutSettings.serviceabilityDemands, {
    requiresAuth: true,
    signal,
  });

  return extractData(payload);
}

export async function fetchDeliverySlots(activeOnly = false, signal) {
  // Support both (signal) and (activeOnly, signal) argument styles
  const isSignal = activeOnly instanceof AbortSignal;
  const actualActiveOnly = isSignal ? false : Boolean(activeOnly);
  const actualSignal = isSignal ? activeOnly : signal;

  const url = typeof endpoints.admin.checkoutSettings.getDeliverySlots === "function"
    ? endpoints.admin.checkoutSettings.getDeliverySlots(actualActiveOnly)
    : endpoints.admin.checkoutSettings.deliverySlots(actualActiveOnly);

  const payload = await getJson(url, {
    requiresAuth: true,
    signal: actualSignal,
  });

  return extractData(payload);
}

export async function createDeliverySlot(slot) {
  const response = await postJson(
    endpoints.admin.checkoutSettings.createDeliverySlot,
    slot,
    { requiresAuth: true }
  );

  return extractFirst(response);
}

export async function updateDeliverySlot(id, slot) {
  const url = typeof endpoints.admin.checkoutSettings.updateDeliverySlot === "function"
    ? endpoints.admin.checkoutSettings.updateDeliverySlot(id)
    : `/order-management-service/checkout-settings/update-delivery-slot/${id}`;

  const response = await putJson(url, slot, {
    requiresAuth: true,
  });

  return extractFirst(response);
}

export async function updateDeliverySlots(payload) {
  const url = endpoints.admin.checkoutSettings.updateDeliverySlots ||
    endpoints.admin.checkoutSettings.deliverySlots(false);
  const items = Array.isArray(payload) ? payload : (payload?.items || payload?.slots || []);
  const data = { items, slots: items };
  const response = await putJson(url, data, {
    requiresAuth: true,
  });

  return extractData(response);
}

export async function toggleDeliverySlotStatus(id, enabled) {
  const url = typeof endpoints.admin.checkoutSettings.updateDeliverySlotStatus === "function"
    ? endpoints.admin.checkoutSettings.updateDeliverySlotStatus(id)
    : endpoints.admin.checkoutSettings.deliverySlotStatus(id);

  const response = await putJson(
    url,
    { enabled },
    { requiresAuth: true }
  );

  return extractFirst(response);
}

export async function deleteDeliverySlot(id) {
  const url = typeof endpoints.admin.checkoutSettings.deleteDeliverySlot === "function"
    ? endpoints.admin.checkoutSettings.deleteDeliverySlot(id)
    : endpoints.admin.checkoutSettings.deliverySlotDelete(id);

  const response = await deleteJson(
    url,
    { requiresAuth: true }
  );

  return extractData(response);
}

export async function resetDefaultDeliverySlots() {
  const url = endpoints.admin.checkoutSettings.resetDefaultDeliverySlots ||
    endpoints.admin.checkoutSettings.deliverySlotsResetDefaults;

  const response = await postJson(
    url,
    {},
    { requiresAuth: true }
  );

  return extractData(response);
}

export async function fetchCustomersPage(filters = {}, signal) {
  const payload = await postJson(
    endpoints.admin.customers.search,
    {
      search: String(filters.search || "").trim(),
      status: filters.status && filters.status !== "all" ? filters.status : "",
      page: Number(filters.page) || 0,
      size: Number(filters.size) || 10,
    },
    {
      requiresAuth: true,
      signal,
    }
  );

  return {
    items: extractData(payload),
    meta: payload?.meta || {},
    page: Number(payload?.page) || 0,
    size: Number(payload?.size) || Number(filters.size) || 10,
    totalElements: Number(payload?.totalElements) || 0,
    totalPages: Number(payload?.totalPages) || 0,
    hasNext: Boolean(payload?.hasNext),
    hasPrevious: Boolean(payload?.hasPrevious),
  };
}

export async function fetchCustomerById(customerId, signal) {
  const payload = await getJson(endpoints.admin.customers.byId(customerId), {
    requiresAuth: true,
    signal,
  });

  return extractFirst(payload);
}
